import { useEffect, useRef, useState } from 'react';
import { Icon } from '@/components/Icon';
import s from './LedgerColumnsPopover.module.css';

export type ColumnChoice = { key: string; title: string; fixed: boolean; permission?: string };

export function LedgerColumnsPopover({ choices, savedKeys, modal = false, touch = false, canSaveAll = false, onSave, onClose }: {
  choices: ColumnChoice[];
  savedKeys: string[] | null;
  modal?: boolean;
  touch?: boolean;
  onSave: (keys: string[] | null, scope: 'mine' | 'all') => Promise<void> | void;
  canSaveAll?: boolean;
  onClose: () => void;
}) {
  const root = useRef<HTMLDivElement>(null);
  const allowed = choices.filter((choice) => !choice.fixed);
  const allowedKeys = new Set(allowed.map((choice) => choice.key));
  const selected = savedKeys ? savedKeys.filter((key) => allowedKeys.has(key)) : allowed.map((choice) => choice.key);
  const [order, setOrder] = useState(() => [...selected, ...allowed.map((choice) => choice.key).filter((key) => !selected.includes(key))]);
  const initialOrder = useRef([...selected, ...allowed.map((choice) => choice.key).filter((key) => !selected.includes(key))]);
  const [visible, setVisible] = useState(() => new Set(selected));
  const [reset, setReset] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dragging = useRef<string | null>(null);

  useEffect(() => {
    const onPointer = (event: PointerEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) onClose();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); onClose(); }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('pointerdown', onPointer); document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const move = (key: string, target: string) => {
    if (key === target) return;
    setReset(false);
    setOrder((old) => {
      const next = old.filter((item) => item !== key);
      const index = next.indexOf(target);
      if (index < 0) return old;
      next.splice(index, 0, key);
      return next;
    });
  };

  const commit = async (scope: 'mine' | 'all') => {
    if (saving) return;
    setSaving(true); setError('');
    try { await onSave(reset ? null : order.filter((key) => visible.has(key)), scope); onClose(); }
    catch (cause) { setError(cause instanceof Error ? cause.message : '列设置保存失败'); }
    finally { setSaving(false); }
  };

  const fixedCount = choices.filter((choice) => choice.fixed).length + (modal ? 1 : 0);
  const displayCount = visible.size + fixedCount;
  const columnCount = choices.length + (modal ? 1 : 0);
  const orderChanges = order.reduce((count, key, index) => count + (initialOrder.current[index] === key ? 0 : 1), 0);
  const restoreDefaults = () => {
    setReset(true);
    setOrder(allowed.map((choice) => choice.key));
    setVisible(new Set(allowed.map((choice) => choice.key)));
  };

  const content = <>
    <header className={`${modal ? s.modalHead : ''} ${touch ? s.touchHead : ''}`}><b>列设置</b>{!modal && <span>已显示 {displayCount} / {columnCount} 列 · 拖动排序</span>}{touch && !modal && <button type="button" onClick={restoreDefaults} disabled={saving}>恢复默认</button>}{modal && <button type="button" className={s.close} aria-label="关闭列设置" onClick={onClose}>×</button>}</header>
    {modal && <div className={s.topSummary}><span>已显示 <b>{displayCount}</b> / {columnCount} 列 · 拖动调整顺序，取消勾选即隐藏</span><button type="button" onClick={restoreDefaults} disabled={saving}>恢复系统默认</button></div>}
    <div className={s.body}>
      <section className={modal ? s.group : undefined}>
      {modal && <div className={s.groupLabel}>固定列 · 不参与排序</div>}
      {choices.filter((choice) => choice.fixed).map((choice) => <div className={`${s.row} ${s.fixed}`} key={choice.key}>
        <span className={s.grip}><Icon name={touch ? 'lock' : 'grip'} size={18} /></span><input type="checkbox" checked readOnly disabled /><span>{choice.title}</span><small>左固定</small>
      </div>)}
      </section>
      <section className={modal ? s.group : undefined}>
      <div className={s.groupLabel}>可调列</div>
      <div className={modal ? s.adjustableList : undefined}>
      {order.map((key) => {
        const choice = allowed.find((item) => item.key === key);
        if (!choice) return null;
        return <div className={s.row} key={key} draggable
          onDragStart={(event) => { dragging.current = key; event.dataTransfer.effectAllowed = 'move'; }}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => { event.preventDefault(); if (dragging.current) move(dragging.current, key); dragging.current = null; }}
          onDragEnd={() => { dragging.current = null; }}>
          <span className={s.grip} aria-hidden="true"><Icon name="grip" size={18} /></span>
          <input type="checkbox" aria-label={`显示${choice.title}`} checked={visible.has(key)} onChange={() => { setReset(false); setVisible((old) => { const next = new Set(old); if (next.has(key)) next.delete(key); else next.add(key); return next; }); }} />
          <span>{choice.title}</span>
          {modal && choice.permission && <small className={s.permissionHint}>{choice.permission}</small>}
          <button type="button" className={s.move} title="上移一列" aria-label={`${choice.title}上移`} disabled={order[0] === key} onClick={() => move(key, order[order.indexOf(key) - 1])}>↑</button>
        </div>;
      })}
      </div>
      </section>
      {modal && <section className={s.group}>
        <div className={s.groupLabel}>固定列 · 不参与排序</div>
        <div className={`${s.row} ${s.fixed}`}><span className={s.grip}><Icon name="grip" size={18} /></span><input type="checkbox" checked readOnly disabled /><span>操作</span><small>右固定</small></div>
      </section>}
      {modal && <div className={s.modalHelp}><Icon name="alert" size={18} /><div><strong>设置只对当前账号生效</strong><p>每个人可以按自己的习惯排列。系统默认顺序由管理员维护，个人设置不受其影响；点「恢复系统默认」可以随时退回。没有权限的列（订单金额、业务员）不会出现在清单里。</p></div></div>}
    </div>
    {error && <p className={s.scope} role="alert">{error}</p>}
    <footer>{modal && <span className={s.modalSummary}>已显示 {displayCount} 列 · 顺序改动 {orderChanges} 处</span>}{!modal && !touch && <button type="button" disabled={saving} onClick={restoreDefaults}>恢复系统默认</button>}<span className={s.spacer} />{modal && <button type="button" onClick={onClose} disabled={saving}>取消</button>}{canSaveAll && <button type="button" disabled={saving} onClick={() => void commit('all')}>设为全员默认</button>}<button type="button" className={s.save} disabled={saving} onClick={() => void commit('mine')}>{saving ? '保存中…' : '保存我的设置'}</button></footer>
  </>;
  return modal ? <div className={s.backdrop} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}><section ref={root} className={s.modal} role="dialog" aria-modal="true" aria-label="台账列设置">{content}</section></div>
    : <div ref={root} className={`${s.popover} ${touch ? s.touchPopover : ''}`} role="dialog" aria-label="台账列设置">{content}</div>;
}
